import React, {useEffect, useMemo, useState} from 'react'
import {Button, Modal, message} from 'antd'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {AlertTriangle, ArrowLeft, ArrowRight, CopyPlus, RefreshCw, RotateCcw, Save} from 'lucide-react'
import {APIv1} from '../../api'
import PaymentProvidersComponent from './NewDeviceFormComponents/PaymentProviderFieldsComponent/PaymentProvidersComponent'
import {getInvalidEnabledPaymentProviders} from './NewDeviceFormComponents/PaymentProviderFieldsComponent/paymentProviderValidation'
import AdditionalFieldsComponent from './NewDeviceFormComponents/AdditionalFieldsComponent'
import MainFieldsComponent from './NewDeviceFormComponents/MainFieldsComponent'
import MarketFieldsComponent from './NewDeviceFormComponents/MarketFieldsComponent/MarketFieldsComponent'
import {useTranslation} from 'react-i18next'
import {
    createCopyFormData,
    createEmptyFormData,
    mapDeviceDetailToFormData,
    parseCopySections,
} from './device.add.formData'
import {
    fetchUserPermissions,
    getStoredUser,
    resolveIsEditPermission,
} from '../../utils'
import {
    canEditField,
    filterEditablePayload,
    resolveUpdateDeviceEditPermission,
    restoreEditableFields,
} from './updateDeviceEditPermissions'
import './AddNewDeviceForm.scss'
import {playFeedbackWave} from "../../utils/feedbackWave";

const cloneFormData = formData => JSON.parse(JSON.stringify(formData))

const AddNewDeviceForm = () => {
    const {t} = useTranslation()
    const {serial_number} = useParams()
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [userData, setUserData] = useState([])
    const [isEditPermission, setIsEditPermission] = useState(false)
    const [isPermissionLoaded, setIsPermissionLoaded] = useState(false)
    const [formData, setFormData] = useState(createEmptyFormData)
    const [initialFormData, setInitialFormData] = useState(createEmptyFormData)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [deviceId, setDeviceId] = useState(null)
    const [pendingSubmitData, setPendingSubmitData] = useState(null)
    const isCopyMode = searchParams.get('mode') === 'copy'
    const copySectionsParam = searchParams.get('sections')
    const editPermission = useMemo(
        () => resolveUpdateDeviceEditPermission(isEditPermission),
        [isEditPermission],
    )

    useEffect(() => {
        if (!serial_number || !userData.token) return

        const fetchDeviceDetails = async () => {
            try {
                const response = await APIv1.get(
                    `/device/status/retrieve/${serial_number}`,
                    {
                        headers: {
                            Authorization: `Token ${userData.token}`,
                        },
                    },
                )

                const mappedFormData = mapDeviceDetailToFormData(response.data)
                setDeviceId(isCopyMode ? null : response.data?.device?.device_id ?? null)

                const nextFormData = isCopyMode
                    ? createCopyFormData(
                        mappedFormData,
                        parseCopySections(copySectionsParam),
                    )
                    : mappedFormData

                setInitialFormData(cloneFormData(nextFormData))
                setFormData(cloneFormData(nextFormData))
            } catch (err) {
                console.error('Something went wrong fetching device details:', err)
            }
        }

        fetchDeviceDetails()
    }, [copySectionsParam, isCopyMode, serial_number, userData.token])

    useEffect(() => {
        const items = getStoredUser()
        if (items && items.token !== userData.token) {
            setUserData(items)
            setIsEditPermission(resolveIsEditPermission(items.data))
            setIsPermissionLoaded(true)
        }
    }, [userData.token])

    useEffect(() => {
        if (!userData.token) return

        const refreshUserPermissions = async () => {
            setIsPermissionLoaded(false)
            const permissions = await fetchUserPermissions(userData)
            setIsEditPermission(permissions.is_edit)
            setIsPermissionLoaded(true)
        }

        refreshUserPermissions()
    }, [userData.data, userData.token])

    const updateSection = (section, values) => {
        if (!isPermissionLoaded) return

        setFormData(prev => {
            const nextFormData = {
                ...prev,
                [section]: {...prev[section], ...values},
            }

            return filterEditablePayload(nextFormData, prev, editPermission)
        })
    }

    const saveDevice = async (submitData, targetDeviceId) => {
        const payload = targetDeviceId
            ? {...submitData, main: {...submitData.main, device_id: targetDeviceId}}
            : submitData

        setIsSubmitting(true)
        try {
            const response = await APIv1.post('/company/device/create/', payload, {
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            })
            if (response.status === 201 || response.status === 200) {
                playFeedbackWave('success');
                message.success(
                    response.status === 200
                        ? t('pages.devices.addNewDeviceColumns.messages.updated')
                        : t('pages.devices.addNewDeviceColumns.messages.created'),
                )
                setInitialFormData(cloneFormData(submitData))
                const savedSerial = submitData.main.device_serial_number
                if (targetDeviceId && savedSerial && savedSerial !== serial_number) {
                    navigate(`/device/detail/${savedSerial}`, {replace: true})
                } else {
                    navigate(-1)
                }
            } else {
                message.info(t('pages.companies.createColumns.messages.info1'))
            }
        } catch (error) {
            console.error('❌ Error saving device:', error)
            playFeedbackWave('failure');
            message.error(getSaveErrorMessage(error))
        } finally {
            setIsSubmitting(false)
        }
    }

    const getSaveErrorMessage = error => {
        const errors = error.response?.status === 400 ? error.response.data : null
        if (!errors || typeof errors !== 'object') {
            return t('pages.devices.addNewDeviceColumns.messages.saveFailed')
        }

        if (errors['main.device_id']) {
            return t('pages.devices.addNewDeviceColumns.messages.deviceNotFound')
        }
        if (errors['main.device_serial_number']) {
            return t('pages.devices.addNewDeviceColumns.messages.serialExists')
        }

        const firstError = Object.values(errors).flat()[0]
        return typeof firstError === 'string'
            ? firstError
            : t('pages.devices.addNewDeviceColumns.messages.saveFailed')
    }

    const handleSubmit = async () => {
        if (!isPermissionLoaded) return

        const submitData = filterEditablePayload(
            formData,
            initialFormData,
            editPermission,
        )

        const invalidEnabledProviders = getInvalidEnabledPaymentProviders(
            submitData.paymentProviders,
        )
        if (invalidEnabledProviders.length > 0) {
            message.warning(
                t('pages.devices.addNewDeviceColumns.paymentProviderActivationError'),
            )
            return
        }

        const isIdentityChanged =
            (submitData.main.device_serial_number ?? '') !==
                (initialFormData.main.device_serial_number ?? '') ||
            (submitData.main.terminal_id ?? '') !==
                (initialFormData.main.terminal_id ?? '')

        if (isEditing && deviceId && isIdentityChanged) {
            setPendingSubmitData(submitData)
            return
        }

        await saveDevice(submitData, isEditing ? deviceId : null)
    }

    const handleIdentityChoice = async shouldUpdate => {
        const submitData = pendingSubmitData
        setPendingSubmitData(null)
        await saveDevice(submitData, shouldUpdate ? deviceId : null)
    }

    const onChangeDatePicker = dates => {
        if (!isPermissionLoaded) return

        if (!dates) {
            updateSection('main', {
                start_date: null,
                end_date: null,
            })
            return
        }

        updateSection('main', {
            start_date: dates[0].startOf('day').format('YYYY-MM-DD HH:mm'),
            end_date: dates[1].endOf('day').format('YYYY-MM-DD HH:mm'),
        })
    }

    const handleClear = () => {
        if (!isPermissionLoaded) return

        setFormData(prev =>
            restoreEditableFields(prev, initialFormData, editPermission),
        )
        message.info('Changes discarded')
    }

    const isFieldEditable = (section, field, container) =>
        isPermissionLoaded && canEditField(section, field, editPermission, container)

    const isEditing = Boolean(serial_number) && !isCopyMode
    const identityChanges = pendingSubmitData
        ? [
            {
                key: 'device_serial_number',
                label: t('pages.devices.addNewDeviceColumns.row6'),
                from: initialFormData.main.device_serial_number,
                to: pendingSubmitData.main.device_serial_number,
            },
            {
                key: 'terminal_id',
                label: t('pages.devices.addNewDeviceColumns.identityTerminalLabel'),
                from: initialFormData.main.terminal_id,
                to: pendingSubmitData.main.terminal_id,
            },
        ].filter(change => (change.from ?? '') !== (change.to ?? ''))
        : []
    const serialLabel = isCopyMode
        ? serial_number
        : formData.main.device_serial_number || serial_number
    const footerText = isCopyMode
        ? t('pages.devices.addNewDeviceColumns.footerCopy', {
            serial: serialLabel,
        })
        : serialLabel
            ? t('pages.devices.addNewDeviceColumns.footerEdit', {
                serial: serialLabel,
            })
            : t('pages.devices.addNewDeviceColumns.footerCreate')

    return (
        <div className='content_container create-device'>
            <div className='create-device__header'>
                <div className='create-device__heading'>
					<span className='create-device__eyebrow'>
						{isCopyMode
                            ? t('pages.devices.addNewDeviceColumns.modeCopy')
                            : isEditing
                                ? t('pages.devices.addNewDeviceColumns.modeEdit')
                                : t('pages.devices.addNewDeviceColumns.modeCreate')}
					</span>
                    <h1>{t('pages.devices.addNewDeviceColumns.title')}</h1>
                    {serialLabel && (
                        <span className='create-device__serial'>#{serialLabel}</span>
                    )}
                </div>
                <Button
                    className='create-device__action-button create-device__action-button--secondary'
                    icon={<ArrowLeft size={16}/>}
                    onClick={() => navigate(-1)}
                >
                    {t('common.backBtn')}
                </Button>
            </div>

            <div className='create-device__forms'>
                <section className='create-device__forms-container'>
                    <div className='create-device__section-header'>
                        <span className='create-device__section-index'>01</span>
                        <h2 className='create-device__forms-title'>
                            {t('pages.devices.addNewDeviceColumns.mainSectionTitle')}
                        </h2>
                    </div>
                    <MainFieldsComponent
                        t={t}
                        data={formData.main}
                        onChange={values => updateSection('main', values)}
                        userData={userData}
                        onChangeDatePicker={onChangeDatePicker}
                        disabled={!isPermissionLoaded}
                        canEditField={field => isFieldEditable('main', field)}
                    />
                </section>

                <section className='create-device__forms-container'>
                    <div className='create-device__section-header'>
                        <span className='create-device__section-index'>02</span>
                        <h2 className='create-device__forms-title'>
                            {t('pages.devices.addNewDeviceColumns.marketSectionTitle')}
                        </h2>
                    </div>
                    <MarketFieldsComponent
                        t={t}
                        data={formData.market}
                        onChange={values => updateSection('market', values)}
                        userData={userData}
                        disabled={!isPermissionLoaded}
                        canEditField={field => isFieldEditable('market', field)}
                    />
                </section>

                <section className='create-device__forms-container'>
                    <div className='create-device__section-header'>
                        <span className='create-device__section-index'>03</span>
                        <h2 className='create-device__forms-title'>
                            {t('pages.devices.addNewDeviceColumns.paymentSectionTitle')}
                        </h2>
                    </div>
                    <PaymentProvidersComponent
                        t={t}
                        data={formData.paymentProviders}
                        onChange={values => updateSection('paymentProviders', values)}
                        disabled={!isPermissionLoaded}
                        canEditField={field =>
                            isFieldEditable('paymentProviders', field)
                        }
                    />
                </section>

                <section className='create-device__forms-container'>
                    <div className='create-device__section-header'>
                        <span className='create-device__section-index'>04</span>
                        <h2 className='create-device__forms-title'>
                            {t('pages.devices.addNewDeviceColumns.configSectionTitle')}
                        </h2>
                    </div>
                    <AdditionalFieldsComponent
                        data={formData.config}
                        onChange={values => updateSection('config', values)}
                        disabled={!isPermissionLoaded}
                        canEditField={(field, container) =>
                            isFieldEditable('config', field, container)
                        }
                    />
                </section>
            </div>

            <div className='create-device__footer'>
				<span className='create-device__footer-copy'>
					{footerText}
				</span>
                <div className='create-device__footer-buttons'>
                    <Button
                        className='create-device__action-button create-device__action-button--primary'
                        icon={<Save size={16}/>}
                        type='primary'
                        size='large'
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={!isPermissionLoaded}
                    >
                        {t('common.submitBtn')}
                    </Button>

                    <Button
                        type='default'
                        className='create-device__action-button create-device__action-button--secondary'
                        icon={<RotateCcw size={16}/>}
                        size='large'
                        onClick={handleClear}
                        disabled={!isPermissionLoaded}
                    >
                        {t('common.clearBtn')}
                    </Button>
                </div>
            </div>

            <Modal
                open={Boolean(pendingSubmitData)}
                onCancel={() => setPendingSubmitData(null)}
                footer={null}
                width={520}
                centered
                getContainer={false}
                className='create-device__identity-modal'
            >
                <div className='create-device__identity-header'>
                    <span className='create-device__identity-icon'>
                        <AlertTriangle size={22}/>
                    </span>
                    <div>
                        <h3 className='create-device__identity-title'>
                            {t('pages.devices.addNewDeviceColumns.identityChangedTitle')}
                        </h3>
                        <p className='create-device__identity-text'>
                            {t('pages.devices.addNewDeviceColumns.identityChangedContent')}
                        </p>
                    </div>
                </div>

                <div className='create-device__identity-changes'>
                    {identityChanges.map(change => (
                        <div key={change.key} className='create-device__identity-change'>
                            <span className='create-device__identity-change-label'>
                                {change.label}
                            </span>
                            <div className='create-device__identity-change-values'>
                                <span className='create-device__identity-value create-device__identity-value--old'>
                                    {change.from || '—'}
                                </span>
                                <ArrowRight size={14}/>
                                <span className='create-device__identity-value create-device__identity-value--new'>
                                    {change.to || '—'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                <div className='create-device__identity-options'>
                    <button
                        type='button'
                        className='create-device__identity-option create-device__identity-option--update'
                        onClick={() => handleIdentityChoice(true)}
                    >
                        <span className='create-device__identity-option-icon'>
                            <RefreshCw size={18}/>
                        </span>
                        <span className='create-device__identity-option-body'>
                            <strong>
                                {t('pages.devices.addNewDeviceColumns.identityChangedUpdate')}
                            </strong>
                            <span>
                                {t('pages.devices.addNewDeviceColumns.identityChangedUpdateHint')}
                            </span>
                        </span>
                    </button>

                    <button
                        type='button'
                        className='create-device__identity-option create-device__identity-option--create'
                        onClick={() => handleIdentityChoice(false)}
                    >
                        <span className='create-device__identity-option-icon'>
                            <CopyPlus size={18}/>
                        </span>
                        <span className='create-device__identity-option-body'>
                            <strong>
                                {t('pages.devices.addNewDeviceColumns.identityChangedCreate')}
                            </strong>
                            <span>
                                {t('pages.devices.addNewDeviceColumns.identityChangedCreateHint')}
                            </span>
                        </span>
                    </button>
                </div>
            </Modal>
        </div>
    )
}

export default AddNewDeviceForm
